'use client';

import * as React from 'react';
import type { TFunction } from 'i18next';

import { Badge, type BadgeVariant } from '@/components/ui/badge';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';

import { bulkEditSelectTriggerClass } from './bulk-edit-field-cell';

const BADGE_CLASS = 'min-w-[4.75rem] justify-center text-[11px] font-medium leading-none';

export interface BulkEditBadgeSelectOption {
  value: string;
  label: string;
  variant: BadgeVariant;
}

interface BulkEditBadgeSelectProps {
  value: string;
  onValueChange: (value: string) => void;
  options: BulkEditBadgeSelectOption[];
}

/** Popover picker — avoids Radix Select stale state in spreadsheet cells. */
export function BulkEditBadgePicker({ value, onValueChange, options }: BulkEditBadgeSelectProps) {
  const [open, setOpen] = React.useState(false);
  const selected = options.find((option) => option.value === value) ?? options[0]!;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" className={bulkEditSelectTriggerClass()} aria-label={selected.label}>
          <Badge variant={selected.variant} className={BADGE_CLASS}>
            {selected.label}
          </Badge>
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-auto p-1">
        {options.map((option) => (
          <button
            key={option.value}
            type="button"
            className={cn(
              'flex w-full items-center rounded-md px-2 py-1.5 text-left transition-colors hover:bg-muted',
              option.value === value && 'bg-muted',
            )}
            onClick={() => {
              onValueChange(option.value);
              setOpen(false);
            }}
          >
            <Badge variant={option.variant} className={BADGE_CLASS}>
              {option.label}
            </Badge>
          </button>
        ))}
      </PopoverContent>
    </Popover>
  );
}

export function BulkEditBadgeSelect({ value, onValueChange, options }: BulkEditBadgeSelectProps) {
  const selected = options.find((option) => option.value === value) ?? options[0]!;

  return (
    <Select value={value} onValueChange={onValueChange}>
      <SelectTrigger className={bulkEditSelectTriggerClass()}>
        <Badge variant={selected.variant} className={BADGE_CLASS}>
          {selected.label}
        </Badge>
      </SelectTrigger>
      <SelectContent>
        {options.map((option) => (
          <SelectItem key={option.value} value={option.value}>
            <Badge variant={option.variant} className={BADGE_CLASS}>
              {option.label}
            </Badge>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

export function buildActiveStatusOptions(t: TFunction): BulkEditBadgeSelectOption[] {
  return [
    { value: 'active', label: t('products:variantsTable.status.active'), variant: 'green' },
    { value: 'inactive', label: t('products:variantsTable.status.inactive'), variant: 'gray' },
  ];
}

export function buildStockStatusOptions(t: TFunction): BulkEditBadgeSelectOption[] {
  return [
    { value: 'in_stock', label: t('products:bulkEditor.inStock'), variant: 'green' },
    { value: 'out_of_stock', label: t('products:bulkEditor.outOfStock'), variant: 'red' },
  ];
}

export function buildYesNoOptions(t: TFunction): BulkEditBadgeSelectOption[] {
  return [
    { value: 'yes', label: t('common:yes', { defaultValue: 'Yes' }), variant: 'green' },
    { value: 'no', label: t('common:no', { defaultValue: 'No' }), variant: 'gray' },
  ];
}

export function buildDefaultVariantOptions(t: TFunction): BulkEditBadgeSelectOption[] {
  return [
    { value: 'yes', label: t('products:variantsTable.default'), variant: 'blue' },
    { value: 'no', label: t('common:no', { defaultValue: 'No' }), variant: 'gray' },
  ];
}

export function buildRecurringOptions(t: TFunction): BulkEditBadgeSelectOption[] {
  return [
    { value: 'one_time', label: t('products:bulkEdit.oneTime'), variant: 'gray' },
    { value: 'recurring', label: t('products:bulkEdit.recurring'), variant: 'blue' },
  ];
}

export function buildIntervalOptions(t: TFunction): BulkEditBadgeSelectOption[] {
  return (['day', 'week', 'month', 'year'] as const).map((interval) => ({
    value: interval,
    label: t(`products:priceForm.${interval}`),
    variant: 'blue' as const,
  }));
}
